import { captureException } from '@sentry/browser';
import { logger } from 'lib/logger';
import { IS_PUBLIC, IS_REACT_PROMO_NEW_YEAR } from 'reactApp/appHelpers/configHelpers';
import { renderLicenseAgreementDialog } from 'reactApp/components/LicenseAgreement/licenseAgreement.toolkit';
import { attachesPageStart } from 'reactApp/modules/attaches/attaches.actions';
import { checkUpdateLicenseCompleted } from 'reactApp/modules/profile/profile.module';
import { getIsCheckUpdateLicenseCompleted } from 'reactApp/modules/profile/profile.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { closeViewer } from 'reactApp/modules/viewer/viewer.module';
import { ViewerSelectors } from 'reactApp/modules/viewer/viewer.selectors';
import { isWelcomeDialogShown, renderWelcomeHelper, renderWelcomeRepeatingHelper } from 'reactApp/modules/welcome/utils';
import { showWelcome } from 'reactApp/modules/welcome/welcome.module';
import { channel } from 'redux-saga';
import { cancel, select, take, takeEvery } from 'redux-saga/effects';

import { EStorageType } from '../storage/storage.types';

function* handleShowWelcome() {
    try {
        const isSocialUser = yield select(UserSelectors.isSocialUser);

        if (isWelcomeDialogShown() || IS_PUBLIC || IS_REACT_PROMO_NEW_YEAR || isSocialUser) {
            return;
        }

        const storage = yield select(getCurrentStorage);
        const isViewerOpening = yield select(ViewerSelectors.isViewerOpening);

        if (isViewerOpening) {
            yield take(closeViewer);
        }

        const isNewbie = yield select(UserSelectors.isNewbie);
        if (!isNewbie) {
            return;
        }

        renderWelcomeHelper({ storage });
    } catch (error) {
        logger.error(error);
        captureException(error);
        yield cancel();
    }
}

function* attachesRepeatWelcome() {
    const isSocialUser = yield select(UserSelectors.isSocialUser);

    if (isWelcomeDialogShown() || IS_PUBLIC || IS_REACT_PROMO_NEW_YEAR || isSocialUser) {
        return;
    }

    const isViewerOpening = yield select(ViewerSelectors.isViewerOpening);
    if (isViewerOpening) {
        yield take(closeViewer);
    }

    const isNewbie = yield select(UserSelectors.isNewbie);
    const isCheckUpdateLicenseCompleted = yield select(getIsCheckUpdateLicenseCompleted);

    if (isNewbie) {
        return;
    }
    if (!isCheckUpdateLicenseCompleted) {
        yield take(checkUpdateLicenseCompleted);
    }
    renderWelcomeRepeatingHelper({ storage: EStorageType.attaches });
}

export function* askLicense({ autoClone }: { autoClone: boolean }) {
    const licenseChannel = channel();
    const storage = yield select(getCurrentStorage);

    if (storage === EStorageType.attaches) {
        renderWelcomeHelper({
            storage,
            onClose: () => licenseChannel.close(),
            onSuccess: () => licenseChannel.put(true),
            publishMode: autoClone,
        });
    } else {
        renderLicenseAgreementDialog({
            onClose: () => licenseChannel.close(),
            onSuccess: () => licenseChannel.put(true),
        });
    }

    const isAgreedWithTheLa = yield take(licenseChannel);
    yield licenseChannel.close();

    return isAgreedWithTheLa;
}

export function* watchWelcomeRoot() {
    yield takeEvery(showWelcome.toString(), handleShowWelcome);
    yield takeEvery(attachesPageStart.toString(), attachesRepeatWelcome);
}
